import React, { useState, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate } from 'react-router-dom';
import { UserDataSelector } from 'store/user';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import useStyles from './styles';
import { ThunkCreateChat } from 'store/chats/thunks/createChat';
import { addTicketChat } from 'store/tickets';
import { setActiveChat } from 'store/chats';
import { ReactComponent as CloseIcon } from 'assets/cross.icon.svg';
import { ChatWithSupportProps, ChatFormValues } from './types';
import { SUGGESTED_QUESTIONS } from './constants';
import { tt } from '@i18n/tt';

const validationSchema = Yup.object().shape({
  message: Yup.string().required('Message is required').min(1, 'Message cannot be empty'),
});

const ChatWithSupport: React.FC<ChatWithSupportProps> = ({ open, onClose, MSBot = true, onSubmit }) => {
  const classes = useStyles();
  const userData = useAppSelector(UserDataSelector);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  const createChat = useCallback(
    (values: ChatFormValues): void => {
      setLoading(true);
      dispatch(ThunkCreateChat({ payload: { ...values, useHelpdeskAi: true } }))
        .unwrap()
        .then(data => {
          dispatch(addTicketChat({ ticketId: data.ticketId.toString(), chatId: +data.id }));
          dispatch(setActiveChat(+data.id));
          if (onSubmit) {
            onSubmit(data);
          }
          navigate(
            MSBot ? `/tickets/${data.ticketId}?compactView=true&screen=chat` : `/chats/${data.id}`,
          );
          onClose();
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [dispatch, navigate, onClose, MSBot],
  );

  const handleSubmit = useCallback(
    (values: ChatFormValues): void => {
      createChat(values);
    },
    [createChat],
  );

  const handleSuggestedQuestionClick = useCallback(
    (question: string): void => {
      createChat({ message: question });
    },
    [createChat],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        aria-labelledby="chat-with-support-title"
        aria-describedby="chat-with-support-description"
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '900px',
          },
        }}
      >
        <IconButton
          aria-label="close chat dialog"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Box className={classes.chatWithSupport}>
            <Box mb={4}>
              <Typography variant="h4" className={classes.title} id="chat-with-support-title">
                {tt('pages.home.supportModal.title', { name: userData?.contactName })}
              </Typography>
            </Box>

            <Formik
              initialValues={{ message: '' }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form
                  className={classes.chatWithSupportMessageInput}
                  aria-label="chat message form"
                >
                  <Paper elevation={2} className={classes.chatWithSupportMessageInput}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="message"
                      placeholder={tt('pages.home.supportModal.field.placeholder')}
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      multiline
                      minRows={1}
                      maxRows={20}
                      disabled={loading}
                      aria-label="Message input"
                      aria-describedby="message-error"
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          const form = (e.target as HTMLInputElement).form;
                          if (form) {
                            form.requestSubmit();
                          }
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      className={classes.sendButton}
                      type="submit"
                      disabled={isSubmitting || loading}
                      aria-label="Send message"
                    >
                      {tt(loading ? 'buttons.sending' : 'buttons.send')}
                    </Button>
                  </Paper>
                </Form>
              )}
            </Formik>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              justifyContent="center"
              mb={4}
              maxWidth={800}
              className={classes.suggestedQuestions}
              role="list"
              aria-label="Suggested questions"
            >
              {SUGGESTED_QUESTIONS.map((q, i) => (
                <Chip
                  key={i}
                  label={tt(q as 'pages.home.supportModal.suggestedQuestions.passwordReset')}
                  clickable
                  onClick={() => {
                    handleSuggestedQuestionClick(tt(q));
                  }}
                  disabled={loading}
                  className={classes.suggestedQuestionChip}
                />
              ))}
            </Stack>
          </Box>
        </DialogContent>
        {loading && <LinearProgress aria-label="Loading" />}
      </Dialog>
    </>
  );
};

ChatWithSupport.displayName = 'ChatWithSupport';

export default memo(ChatWithSupport);
